/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    MONGODB_LOGGING_PHASE1
 * Date:         2025-12-20
 * Modified By:  Cursor
 *
 * Description:
 * MongoDB log API client service for admin log viewer.
 * Replaces file-based log API calls with MongoDB queries.
 *
 * Related Screens / Modules:
 * - client/src/pages/admin/LogsPage.tsx
 * - server/routes/admin/mongoLogs.js
 */

import axios from "axios";
import { apiUrl } from "../config/environment";
import { getAuthHeaders, getToken } from "../utils/authUtils";

// Service type definition
export type ServiceType = "server" | "mailer" | "uploader";

// Log level type
export type LogLevel = "error" | "warn" | "info" | "debug" | "all";

// MongoDB log entry interface
export interface MongoLogEntry {
  _id: string;
  timestamp: string | Date;
  level: string;
  message: string;
  service: ServiceType;
  metadata?: any;
  expiresAt?: string | Date;
  [key: string]: any;
}

// Query parameters interface
export interface MongoLogsQuery {
  service?: ServiceType;
  level?: LogLevel;
  startDate?: string;
  endDate?: string;
  search?: string;
  page?: number;
  limit?: number;
}

// Statistics query parameters
export interface MongoLogsStatsQuery {
  service?: ServiceType;
  startDate?: string;
  endDate?: string;
}

// Recent logs query parameters
export interface MongoLogsRecentQuery {
  service?: ServiceType;
  level?: LogLevel;
  limit?: number;
}

// Search query parameters
export interface MongoLogsSearchQuery {
  query: string;
  service?: ServiceType;
  level?: LogLevel;
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
}

// Pagination metadata
export interface PaginationMeta {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

// Logs response interface
export interface MongoLogsResponse {
  success: boolean;
  data: {
    logs: MongoLogEntry[];
    pagination: PaginationMeta;
    filters?: MongoLogsQuery;
  };
}

// Statistics response interface
export interface MongoLogsStatsResponse {
  success: boolean;
  data: {
    total: number;
    byLevel: Record<string, number>;
    byService: Record<string, number>;
    byHour: Array<{
      hour: string;
      count: number;
    }>;
  };
}

// Recent logs response interface
export interface MongoLogsRecentResponse {
  success: boolean;
  data: {
    logs: MongoLogEntry[];
  };
}

// Log by ID response interface
export interface MongoLogByIdResponse {
  success: boolean;
  data: {
    log: MongoLogEntry;
  };
}

/**
 * Get logs with filtering and pagination
 * Replaces: GET /api/admin/logs and GET /api/admin/logs/service
 */
export async function getMongoLogs(
  params: MongoLogsQuery = {}
): Promise<MongoLogsResponse> {
  const response = await axios.get<MongoLogsResponse>(
    `${apiUrl}/api/admin/logs/mongodb`,
    {
      params,
      ...getAuthHeaders(),
    }
  );
  return response.data;
}

/**
 * Get log statistics
 * Replaces: File count/size stats
 */
export async function getMongoLogStats(
  params: MongoLogsStatsQuery = {}
): Promise<MongoLogsStatsResponse> {
  const response = await axios.get<MongoLogsStatsResponse>(
    `${apiUrl}/api/admin/logs/mongodb/stats`,
    {
      params,
      ...getAuthHeaders(),
    }
  );
  return response.data;
}

/**
 * Get recent logs (tail functionality replacement)
 * Replaces: GET /api/admin/logs/:filename/tail
 */
export async function getRecentMongoLogs(
  params: MongoLogsRecentQuery = {}
): Promise<MongoLogsRecentResponse> {
  const response = await axios.get<MongoLogsRecentResponse>(
    `${apiUrl}/api/admin/logs/mongodb/recent`,
    {
      params,
      ...getAuthHeaders(),
    }
  );
  return response.data;
}

/**
 * Export logs as JSON or CSV
 * Replaces: GET /api/admin/logs/:filename/download
 */
export async function exportMongoLogs(
  params: MongoLogsQuery & { format?: "json" | "csv" } = {}
): Promise<void> {
  const { format = "json", ...queryParams } = params;
  // [2026-07-08] cookie-auth-fix: cookie authenticates; no localStorage token required
  const token = getToken();

  const url = `${apiUrl}/api/admin/logs/mongodb/export?format=${format}&${new URLSearchParams(
    Object.entries(queryParams).reduce((acc, [key, value]) => {
      if (value !== undefined && value !== null) {
        acc[key] = String(value);
      }
      return acc;
    }, {} as Record<string, string>)
  ).toString()}`;

  try {
    const response = await fetch(url, {
      // [2026-07-08] cookie-auth-fix: send cookie with fetch (axios defaults don't apply to fetch)
      credentials: "include",
      headers: {
        Authorization: token || "",
      },
    });

    if (!response.ok) {
      throw new Error(`Export failed: ${response.statusText}`);
    }

    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = blobUrl;
    
    const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
    const filename = `logs-${timestamp}.${format}`;
    link.download = filename;
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
  } catch (error) {
    console.error("Export error:", error);
    throw error;
  }
}

/**
 * Get specific log entry by ID
 */
export async function getMongoLogById(logId: string): Promise<MongoLogByIdResponse> {
  const response = await axios.get<MongoLogByIdResponse>(
    `${apiUrl}/api/admin/logs/mongodb/${logId}`,
    {
      ...getAuthHeaders(),
    }
  );
  return response.data;
}

/**
 * Search logs with text search
 */
export async function searchMongoLogs(
  params: MongoLogsSearchQuery
): Promise<MongoLogsResponse> {
  const response = await axios.post<MongoLogsResponse>(
    `${apiUrl}/api/admin/logs/mongodb/search`,
    params,
    {
      ...getAuthHeaders(),
    }
  );
  return response.data;
}

// [2026-07-08] redeploy: re-commit cookie-auth fix to trigger rebuild
